import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useListOrgsQuery } from '@proaxiv/server-api'
import type { ListOrganizationsResponse } from '@proaxiv/server-api'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { OrgDetailView } from './OrganizationDetailPage'

const BuildingPlusIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18M3 9h18M3 15h18" />
    <circle cx="18" cy="18" r="5" fill="currentColor" stroke="white" strokeWidth="1.5" />
    <path d="M18 15.5v5M15.5 18h5" stroke="white" strokeWidth="1.5" />
  </svg>
)

/** Shown when the user has no organization membership. */
function NoOrganizationView() {
  const { t } = useTranslation()
  return (
    <div className="mx-auto flex max-w-5xl flex-col items-center justify-center px-4 py-20 md:px-6">
      <div className="border-pax-purple/20 flex w-full max-w-md flex-col items-center gap-6 rounded-2xl border bg-white p-10 text-center shadow-sm">
        <div className="bg-pax-light-blue text-pax-blue flex h-16 w-16 items-center justify-center rounded-2xl">
          <BuildingPlusIcon />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-pax-black text-xl font-bold">{t('no_org_title')}</h1>
          <p className="text-pax-gray text-sm leading-relaxed">{t('no_org_body')}</p>
        </div>
        <Link
          to="/organizations/new"
          className="bg-pax-blue hover:bg-pax-blue/90 inline-flex w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition-colors focus-visible:outline-none"
        >
          {t('no_org_cta')}
        </Link>
      </div>
    </div>
  )
}

/**
 * Home page — renders the currently selected organization's detail view.
 *
 * - No orgs: shows a "create your first org" call-to-action.
 * - Active org set: renders `OrgDetailView` for that org (edit controls visible only to owners).
 */
export function HomePage() {
  const { t } = useTranslation()
  const { activeOrgId } = useActiveOrg()
  const { data: rawData, isLoading, isFetching } = useListOrgsQuery({})
  const orgs = (rawData as ListOrganizationsResponse | undefined)?.organizations ?? []

  // Show a loader on first fetch OR while refetching with an empty result (e.g. right
  // after createOrg/join invalidates the cache but the refetch hasn't landed yet).
  if (isLoading || (isFetching && orgs.length === 0)) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-center px-4 py-20 md:px-6">
        <div className="text-pax-gray text-sm">{t('loading')}</div>
      </div>
    )
  }

  if (orgs.length === 0) {
    return <NoOrganizationView />
  }

  // Resolve the org to display: use activeOrgId if valid, otherwise fall back to the first org.
  const orgId =
    activeOrgId && orgs.some((o) => o.orgId === activeOrgId) ? activeOrgId : orgs[0].orgId

  return <OrgDetailView orgId={orgId} />
}
